import { motion } from "framer-motion";
import { Lightning, ArrowUp, ArrowDown, Minus, Notebook, ArrowsClockwise, CalendarBlank } from "@phosphor-icons/react";

export function formatNum(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return n.toLocaleString();
  return String(n);
}

export function formatWeekLabel(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch (e) {
    console.error("Date parse failed:", e);
    return iso;
  }
}

export function HBar({ value, max, label, count }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="w-[160px] shrink-0 text-xs text-[var(--text-secondary)] truncate text-right">{label}</span>
      <div className="flex-1 h-7 bg-[var(--bg-hover)] rounded overflow-hidden relative">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="h-full rounded bg-gradient-to-r from-[#FF4400] to-[#FF6633]"
        />
      </div>
      <span className="w-[40px] text-right text-xs font-['JetBrains_Mono'] text-[var(--text-primary)]">{count}</span>
    </div>
  );
}

export function DonutChart({ segments }) {
  const total = segments.reduce((a, b) => a + b.count, 0);
  if (total === 0) return null;
  const COLORS = ["#FF4400", "#FF6633", "#FF8855", "#FFAA77", "#FFCC99", "#FFE0CC"];
  let cumulative = 0;
  const radius = 40;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="flex items-center gap-6">
      <svg viewBox="0 0 100 100" className="w-32 h-32 shrink-0" data-testid="donut-chart">
        {segments.map((seg, i) => {
          const pct = seg.count / total;
          const dashLen = pct * circumference;
          const offset = cumulative * circumference;
          cumulative += pct;
          return (
            <circle
              key={seg.label}
              cx="50" cy="50" r={radius}
              fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth="14"
              strokeDasharray={`${dashLen} ${circumference - dashLen}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 50 50)"
              className="transition-all duration-700"
            />
          );
        })}
      </svg>
      <div className="flex flex-col gap-1.5">
        {segments.map((seg, i) => (
          <div key={seg.label} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
            <span className="text-xs text-[var(--text-secondary)] truncate max-w-[120px]">{seg.label}</span>
            <span className="text-xs font-['JetBrains_Mono'] text-[var(--text-muted)]">{seg.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function StatCard({ label, value, trend, icon: Icon, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35 }}
      className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5 flex flex-col gap-1"
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center justify-between">
        <Icon size={18} className="text-[var(--text-muted)]" />
        {trend !== undefined && trend !== null && (
          <span className={`flex items-center gap-0.5 text-[10px] font-['JetBrains_Mono'] ${trend > 0 ? "text-emerald-400" : trend < 0 ? "text-red-400" : "text-[var(--text-muted)]"}`}>
            {trend > 0 ? <ArrowUp size={10} /> : trend < 0 ? <ArrowDown size={10} /> : <Minus size={10} />}
            {trend > 0 ? "+" : ""}{trend}%
          </span>
        )}
      </div>
      <span className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] font-['JetBrains_Mono'] tracking-tight">{value}</span>
      <span className="text-xs text-[var(--text-muted)]">{label}</span>
    </motion.div>
  );
}
